import 'twin.macro'
import { useMultipleSelection, useSelect } from 'downshift'
import type { ReactElement, Ref } from 'react'
import { forwardRef } from 'react'

import {
  DropdownWrapper,
  DropdownButton,
  DropdownList,
  DropdownListItem,
  DropdownTitle,
  DropdownSubtitle,
  DropdownArrow,
} from '../DropdownAtoms/DropdownAtoms'
import Checkbox from '../Checkbox/Checkbox'
import type { MultiSelectProps } from './types'
import itemToItemTitle from '../../internal/itemToItemTitle'
import type { DropdownItem } from '../DropdownAtoms/types'

function MultiSelect<ItemValue>(
  {
    items,
    placeholder,
    selectedItems,
    initialSelectedItems = [],
    onSelectedItemsChange,
    onBlur,
    name,
    error,
    disabled,
    ...formFieldForwardedProps
  }: MultiSelectProps<ItemValue>,
  ref: Ref<HTMLButtonElement>,
) {
  const {
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems: localSelectedItems,
  } = useMultipleSelection({
    ...(selectedItems ? { selectedItems } : { initialSelectedItems }),
    onSelectedItemsChange,
    itemToString: itemToItemTitle,
  })

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    selectedItem: null,
    initialHighlightedIndex: 0,
    items,
    itemToString: itemToItemTitle,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges

      switch (type) {
        // Keeps option menu open on selection
        case useSelect.stateChangeTypes.MenuKeyDownEnter:
        case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            // Force highlighted item to stay put so user can toggle/untoggle
            // Otherwise, the highlighted item is set to -1 once clicked
            // (as per Downshift's default behaviour)
            highlightedIndex: items.findIndex((i) => {
              return i.value === changes?.selectedItem?.value
            }),
            isOpen: true,
          }

        default:
          break
      }

      return changes
    },

    onStateChange: ({ type, selectedItem }) => {
      if (!selectedItem) {
        return
      }

      switch (type) {
        case useSelect.stateChangeTypes.MenuKeyDownEnter:
        case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          if (isSelected(selectedItem)) {
            removeSelectedItem(selectedItem)

            return
          }

          addSelectedItem(selectedItem)
          break

        default:
          break
      }
    },
  })

  function isSelected(item: DropdownItem<ItemValue>) {
    return localSelectedItems.some((i) => i.value === item.value)
  }

  const buttonText =
    localSelectedItems.length > 0
      ? localSelectedItems
          .map((i) => i.title)
          .sort((a, b) => {
            if (a < b) return -1
            if (a > b) return 1

            return 0
          })
          .join(', ')
      : placeholder

  return (
    <DropdownWrapper>
      <DropdownButton
        $isPlaceholder={localSelectedItems.length === 0}
        $hasError={error}
        {...getToggleButtonProps(
          getDropdownProps({
            name,
            ref,
            onBlur,
            disabled,
            ...formFieldForwardedProps,
            // Force undefined labelledby so the screen reader actually reads the button content.
            // TODO: Check if this is an actual downshift bug or some implementation error of ours
            'aria-labelledby': undefined,
          }),
        )}
      >
        {buttonText}
        <DropdownArrow />
      </DropdownButton>

      <DropdownList
        {...getMenuProps({
          onKeyDown(e) {
            // prevent the esc keypress from bubbling up the dom
            // useful to close the menu instead of closing a whole dialog
            if (e.key === 'Escape') e.stopPropagation()
          },
        })}
        $isVisible={isOpen}
        tw="mt-1.5 absolute"
      >
        {items.map((item, index) => {
          const itemIsSelected = isSelected(item)

          return (
            <DropdownListItem
              key={`${item.value}${index}`}
              startSlot={
                <Checkbox checked={itemIsSelected} readOnly tabIndex={-1} />
              }
              hasSubtitle={Boolean(item.subtitle)}
              {...getItemProps({
                item,
                index,
                disabled: item.disabled,
                isSelected: itemIsSelected,
                'aria-disabled': item.disabled,
              })}
              isHighlighted={highlightedIndex === index}
              tw="items-center"
            >
              {item.title && (
                <DropdownTitle $isSelected={itemIsSelected}>
                  {item.title}
                </DropdownTitle>
              )}
              {item.subtitle && (
                <DropdownSubtitle $isSelected={itemIsSelected}>
                  {item.subtitle}
                </DropdownSubtitle>
              )}
            </DropdownListItem>
          )
        })}
      </DropdownList>
    </DropdownWrapper>
  )
}

// This ugly thing is needed because we need to pass a generic (ItemValue) to the component
// but forwardRef doesnt support that
export default forwardRef(MultiSelect) as <Value>(
  props: MultiSelectProps<Value> & { ref?: Ref<HTMLButtonElement> },
) => ReactElement
